import styles from './Filter.module.css';

import React from 'react';

import { DateTime } from 'luxon';

import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';

import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Icon from '@mui/material/Icon';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import ExpandMore from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';
import Search from '@mui/icons-material/Search';

export const Filter = ({ filters, onFilterChange, onClearFilters }) => {
    const [ isExpanded, setIsExpanded ] = React.useState(false);

    const toggleExpanded = () => setIsExpanded(!isExpanded);

    const handleInputChange = (field, value) => {
        onFilterChange({ ...filters, [ field ]: value });
    };

    const daysOfWeek = [
        { value: 'sunday', label: 'Sun' },
        { value: 'monday', label: 'Mon' },
        { value: 'tuesday', label: 'Tue' },
        { value: 'wednesday', label: 'Wed' },
        { value: 'thursday', label: 'Thu' },
        { value: 'friday', label: 'Fri' },
        { value: 'saturday', label: 'Sat' }
    ];

    const hasActiveFilters = filters.search || filters.days.length > 0 || filters.onlyAvailable || filters.startDate || filters.endDate;

    return (
        <div className={ styles.filterContainer }>
            <div className={ styles.filterHeader } onClick={ toggleExpanded }>
                <span>
                    { hasActiveFilters && <span className={ styles.activeIndicator }>●&nbsp;</span> }
                    <Icon component={ Search } sx={ { mr: 1 } } />
                    <Typography>Search</Typography>
                </span>
                <span>
                    <Icon>{ isExpanded ? <ExpandLess /> : <ExpandMore /> }</Icon>
                </span>
            </div>

            <div className={ `${ styles.filterContent } ${ isExpanded ? styles.expanded : styles.collapsed }` }>
                <div className={ styles.searchSection }>
                    <input
                        type="text"
                        placeholder="Search by name, event, or time (e.g., 'A', 'Race Day', '09:00')"
                        value={ filters.search }
                        onChange={ (e) => handleInputChange('search', e.target.value) }
                        className={ styles.searchInput }
                    />
                </div>

                <div className={ styles.availabilitySection }>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={ filters.onlyAvailable || false }
                                onChange={ (e) => handleInputChange('onlyAvailable', e.target.checked) }
                            />
                        }
                        label="Only Available Shifts"
                    />
                </div>

                <Divider sx={ { my: 4 } } />

                <div className={ styles.dateSection }>
                    <LocalizationProvider dateAdapter={ AdapterLuxon }>
                        <DatePicker
                            label="Start Date"
                            value={ filters.startDate ? DateTime.fromISO(filters.startDate) : null }
                            onChange={ (newDate) => onFilterChange({ ...filters, startDate: newDate ? newDate.toISODate() : null }) }
                        />
                        <DatePicker
                            label="End Date"
                            value={ filters.endDate ? DateTime.fromISO(filters.endDate) : null }
                            onChange={ (newDate) => onFilterChange({ ...filters, endDate: newDate ? newDate.toISODate() : null }) }
                        />
                    </LocalizationProvider>
                </div>

                <Divider sx={ { my: 4 } } />

                <div className={ styles.daysSection }>
                    <div className={ styles.dayChips }>
                        { daysOfWeek.map(day => (
                            <button
                                key={ day.value }
                                type="button"
                                className={ `${ styles.dayChip } ${ filters.days.includes(day.value) ? styles.active : ''
                                    }` }
                                onClick={ () => {
                                    const newDays = filters.days.includes(day.value)
                                        ? filters.days.filter(d => d !== day.value)
                                        : [ ...filters.days, day.value ];
                                    handleInputChange('days', newDays);
                                } }
                            >
                                { day.label }
                            </button>
                        )) }
                    </div>
                </div>

                <Divider sx={{ mt: 4, mb: 2 }} />

                <div className={ styles.buttonsSection }>
                    <Stack direction="column" justifyContent='flex-start' alignItems="center">
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={ filters.excludeSearchParams || false }
                                    onChange={ (e) => handleInputChange('excludeSearchParams', e.target.checked) }
                                    disabled={ !hasActiveFilters }
                                />
                            }
                            label="Exclude search parameters from filter criteria"
                        />

                        <Typography variant="caption" color="textSecondary">
                            e.g. "Dec" will exclude any shifts in December
                        </Typography>
                    </Stack>

                    <Button
                        variant={ hasActiveFilters ? 'contained' : 'outlined' }
                        className={ styles.clearButton }
                        onClick={ onClearFilters }
                        disabled={ !hasActiveFilters }
                        color='secondary'
                    >
                        Clear Filters
                    </Button>
                </div>
            </div>
        </div>
    );
};